import type { Circuit } from './LapTimer';

/**
 * Circuit registry for automatic lap timing. Finish-line endpoints (finishA/B)
 * span ~14 m perpendicular to the racing line at the timing point; `headingDeg`
 * is the direction of travel across it. Derived from OpenStreetMap raceway
 * centerlines cross-checked with lap-timer/circuit databases. The LapTimer
 * auto-selects the nearest circuit by GPS.
 */

/** Build a circuit from its start/finish point and the direction of travel across it (gate = 2 × halfWidthM). */
function sf(id: string, name: string, lat: number, lon: number, headingDeg: number, lapLengthM: number, radiusM = 2500, halfWidthM = 20): Circuit {
  const dLat = (halfWidthM / 111320);
  const dLon = (halfWidthM / (111320 * Math.cos((lat * Math.PI) / 180)));
  // the line is perpendicular to the heading
  const perp = ((headingDeg + 90) * Math.PI) / 180;
  const finishA = { lat: lat + Math.cos(perp) * dLat, lon: lon + Math.sin(perp) * dLon };
  const finishB = { lat: lat - Math.cos(perp) * dLat, lon: lon - Math.sin(perp) * dLon };
  return { id, name, center: { lat, lon }, radiusM, finishA, finishB, headingDeg, lapLengthM };
}

const GENERATED: Circuit[] = [
  // Start/finish points snapped onto the OpenStreetMap raceway centreline (2026-08-25): the S/F estimate is the
  // midpoint of the circuit's pit lane, projected onto the track; direction of travel from OSM oneway tags
  // where present. Lines marked "approximate" could not be snapped and keep a hand-placed point.
  sf('jarama', 'Circuito del Jarama', 40.615728, -3.583597, 162, 3850),
  sf('jerez', 'Circuito de Jerez', 36.708466, -6.031881, 337, 4423),
  sf('valencia-rt', 'Circuit Ricardo Tormo', 39.484689, -0.628935, 55, 4005),
  sf('aragon', 'MotorLand Aragón', 41.078937, -0.198861, 309, 5345),
  sf('navarra', 'Circuito de Navarra', 42.458500, -1.739000, 110, 3933),
  sf('ascari', 'Ascari Race Resort', 36.776800, -5.216200, 230, 5425),
  sf('guadix', 'Circuito de Guadix', 37.283000, -3.085000, 100, 3000, 2000),
  sf('portimao', 'Autódromo do Algarve', 37.229587, -8.629902, 344, 4653),
  sf('estoril', 'Circuito do Estoril', 38.749021, -9.393095, 204, 4182),
  sf('paul-ricard', 'Circuit Paul Ricard', 43.25060, 5.79170, 70, 5842), // approximate (not snapped)
  sf('magny-cours', 'Circuit de Nevers Magny-Cours', 46.864632, 3.164712, 51, 4411),
  sf('le-mans-bugatti', 'Le Mans Bugatti', 47.945775, 0.209257, 345, 4185),
  sf('dijon', 'Dijon-Prenois', 47.362547, 4.896425, 45, 3801),
  sf('spa', 'Spa-Francorchamps', 50.442513, 5.970476, 129, 7004, 3500),
  sf('zandvoort', 'Circuit Zandvoort', 52.390112, 4.541628, 22, 4259),
  sf('zolder', 'Circuit Zolder', 50.988331, 5.253983, 237, 4011),
  sf('nurburgring-gp', 'Nürburgring', 50.335409, 6.947475, 225, 5148, 6000),
  sf('hockenheim', 'Hockenheimring', 49.328421, 8.565357, 335, 4574),
  sf('lausitzring', 'Lausitzring', 51.534622, 13.926646, 253, 4534),
  sf('red-bull-ring', 'Red Bull Ring', 47.220113, 14.765733, 255, 4318),
  sf('salzburgring', 'Salzburgring', 47.822835, 13.167199, 256, 4241),
  sf('brno', 'Brno Circuit', 49.203142, 16.444427, 113, 5403),
  sf('slovakia-ring', 'Slovakia Ring', 47.992500, 17.571300, 280, 5922),
  sf('hungaroring', 'Hungaroring', 47.578920, 19.248623, 308, 4381),
  sf('monza', 'Autodromo Nazionale Monza', 45.617301, 9.280931, 6, 5793, 3500),
  sf('imola', 'Imola', 44.344047, 11.716977, 270, 4909),
  sf('mugello', 'Mugello Circuit', 43.998068, 11.371785, 25, 5245),
  sf('misano', 'Misano World Circuit', 43.961401, 12.683897, 204, 4226),
  sf('vallelunga', 'Vallelunga', 42.156153, 12.369292, 350, 4085),
  sf('silverstone', 'Silverstone', 52.069112, -1.022431, 37, 5891, 3500),
  sf('brands-hatch', 'Brands Hatch', 51.359872, 0.258888, 62, 3908, 2000),
  sf('donington', 'Donington Park', 52.829931, -1.380363, 281, 4020),
  sf('oulton-park', 'Oulton Park', 53.179076, -2.612454, 167, 4307),
  sf('snetterton', 'Snetterton', 52.463394, 0.944885, 265, 4779),
  sf('laguna-seca', 'WeatherTech Raceway Laguna Seca', 36.586301, -121.756776, 213, 3602),
  sf('cota', 'Circuit of The Americas', 30.132676, -97.640983, 128, 5513, 3500),
  sf('daytona-road', 'Daytona International Speedway (road)', 29.187890, -81.072630, 220, 5729, 3500),
  sf('road-atlanta', 'Michelin Raceway Road Atlanta', 34.493100, -83.817300, 110, 4088),
  sf('suzuka', 'Suzuka Circuit', 34.843771, 136.539861, 140, 5807, 3500),
  sf('fuji', 'Fuji Speedway', 35.372474, 138.927529, 51, 4563),
  sf('bathurst', 'Mount Panorama Bathurst', -33.449700, 149.550000, 200, 6213, 3500),
  sf('interlagos', 'Interlagos', -23.705987, -46.699339, 160, 4309),
  sf('yas-marina', 'Yas Marina Circuit', 24.464735, 54.605539, 127, 5281),
  sf('kyalami', 'Kyalami', -25.999468, 28.069206, 42, 4522),
  sf('watkins-glen', 'Watkins Glen International', 42.342486, -76.928704, 4, 5472),
  sf('road-america', 'Road America', 43.798090, -87.989634, 179, 6515, 3500),
  sf('sebring', 'Sebring International Raceway', 27.450230, -81.352677, 90, 6020, 3000),
  sf('sonoma', 'Sonoma Raceway', 38.160242, -122.453106, 342, 4060),
  sf('mid-ohio', 'Mid-Ohio Sports Car Course', 40.37920, -82.62940, 90, 3860), // approximate (not snapped)
  sf('vir', 'Virginia International Raceway', 36.568236, -79.208893, 63, 5263, 3000),
  sf('barber', 'Barber Motorsports Park', 33.532230, -86.620137, 46, 3830),
  sf('lime-rock', 'Lime Rock Park', 41.928830, -73.381703, 114, 2414, 1500),
  sf('indy-road', 'Indianapolis Motor Speedway (road)', 39.801913, -86.234138, 269, 3925, 3000),
  sf('portland', 'Portland International Raceway', 45.594713, -122.693364, 291, 3170),
  sf('thunderhill', 'Thunderhill Raceway Park', 39.540218, -122.331193, 180, 4830),
  sf('buttonwillow', 'Buttonwillow Raceway Park', 35.488707, -119.544354, 87, 4800),
  sf('willow-springs', 'Willow Springs International Raceway', 34.871310, -118.263310, 304, 4023),
  sf('homestead-road', 'Homestead-Miami Speedway (road)', 25.453118, -80.404252, 1, 3700),
  sf('nola', 'NOLA Motorsports Park', 29.88800, -90.22100, 90, 4400), // approximate (not snapped)
  sf('utah', 'Utah Motorsports Campus', 40.583158, -112.374935, 270, 4900, 3000),
  sf('charlotte-roval', 'Charlotte Motor Speedway (ROVAL)', 35.353992, -80.684608, 191, 3660, 2500),
  sf('summit-point', 'Summit Point Motorsports Park', 39.237875, -77.967900, 199, 3200),
  sf('njmp-thunderbolt', 'New Jersey Motorsports Park (Thunderbolt)', 39.361021, -75.074170, 121, 3620),
  sf('ridge', 'The Ridge Motorsports Park', 47.254716, -123.193957, 91, 3860),
  sf('chuckwalla', 'Chuckwalla Valley Raceway', 33.68000, -115.43800, 90, 4300), // approximate (not snapped)
  sf('pittsburgh', 'Pittsburgh International Race Complex', 40.850439, -80.348725, 66, 4500),
  sf('ctmp', 'Canadian Tire Motorsport Park', 44.054101, -78.673058, 108, 3957),
  sf('mont-tremblant', 'Circuit Mont-Tremblant', 46.193450, -74.611162, 60, 4260),
  sf('gilles-villeneuve', 'Circuit Gilles Villeneuve', 45.500015, -73.522690, 169, 4361),
  sf('hermanos-rodriguez', 'Autódromo Hermanos Rodríguez', 19.406207, -99.094300, 98, 4304),
];
export const CIRCUITS: Circuit[] = [
  {
    id: 'montmelo',
    name: 'Circuit de Barcelona-Catalunya',
    center: { lat: 41.56961, lon: 2.25796 },
    radiusM: 2500,
    finishA: { lat: 41.570209, lon: 2.26148 },
    finishB: { lat: 41.570276, lon: 2.261338 },
    headingDeg: 212, // clockwise, SW-bound across the main straight
    lapLengthM: 4675,
  },
  {
    id: 'castelloli',
    name: 'Parcmotor Castellolí',
    center: { lat: 41.58997, lon: 1.69009 },
    radiusM: 2000,
    finishA: { lat: 41.587555, lon: 1.692047 },
    finishB: { lat: 41.587477, lon: 1.692179 },
    headingDeg: 38, // clockwise, NE-bound on the ~600 m main straight
    lapLengthM: 4030,
  },
  {
    id: 'calafat',
    name: 'Circuit de Calafat',
    center: { lat: 40.93318, lon: 0.84179 },
    radiusM: 1800,
    finishA: { lat: 40.932536, lon: 0.839693 },
    finishB: { lat: 40.932451, lon: 0.839816 },
    // Direction unverified in sources (OSM has no oneway tag). If a real lap
    // triggers on the wrong pass, flip to 42. Endpoints stay the same.
    headingDeg: 222,
    lapLengthM: 3250,
  },

  // ---- generated: start/finish point + direction of travel, 40 m gate (approximate S/F from public maps;
  //      the gate is wide on purpose — refine finishA/B on the first visit if a lap is missed) ----
  ...GENERATED,
];
